import React, { useEffect } from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import _cloneDeep from 'lodash/cloneDeep';
import _flatMap from 'lodash/flatMap';
import _includes from 'lodash/includes';
import { TOAST_TIMEOUT } from 'src/config/Toast';
import {
  getRegionTasksById,
  setIsCancelModalOpen,
  setIsRestrictionsPageReadOnly,
  setPageProgress,
  setRestrictionListItems,
  setSelectedTasks,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { type JmrTask } from 'src/store/newJmrPageSlice/types';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { closeAllToastsForMessage, createToastIfNoneForMessage } from 'src/store/toastsSlice';
import { isNotEmpty } from 'src/utils/helpers';
import { JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import {
  isParentTask,
  isRestrictionListTask,
  type RestrictionListItem,
  validateRestrictionListItems,
} from 'src/utils/restrictionUtilsLegacy';
import { setupPreselectedRestrictionListItems } from 'src/utils/restrictionUtilsLegacy';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJmrPageTitle, NewJMRPageWrapper } from '../pageLayout/pageLayout';
import PlacementCountAlert from '../placementCountAlert/placementCountAlert';

import RestrictionList from './restrictionList';

import AllTasksSelector from './selectors/allTasksSelector';

// **** Constants **** //

// Exporting for texting
export const TOAST_MESSAGE = 'Please see the errors below, and try clicking "Next" again';

// **** Functional Components **** //

/**
 * Main component.
 */
const RestrictionsPage = () => {
  const dispatch = useAppDispatch();
  const {
    restrictionListItems,
    isRestrictionsPageReadOnly,
    jmrFlowType,
    selectedTasks,
    consolidatedRegionTasks,
    backgroundFormData,
  } = useAppSelector((state) => state.newJmrPage);

  // If updating a Placement, set the list of pre-selected items when the group of
  // selected-tasks change
  useEffect(() => {
    const { CompleteTransfer, UpdateDates, AlternativePlacementRecommended } = JmrFlowTypes;
    const shouldReuseExistingRestrictions = _includes(
      [CompleteTransfer, UpdateDates, AlternativePlacementRecommended],
      jmrFlowType,
    );
    if (selectedTasks.length > 0 && shouldReuseExistingRestrictions) {
      const items = setupPreselectedRestrictionListItems(selectedTasks, consolidatedRegionTasks);
      dispatch(setRestrictionListItems(items));
    }
  }, [consolidatedRegionTasks, selectedTasks]);

  // Fetch tasks based on region id of the selected site. Note that the regionTasks
  // is used on the restrictions-pages for the list of tasks (aka restrictions).
  useEffect(() => {
    const regionId = backgroundFormData.site?.regionId;
    if (regionId !== undefined) {
      dispatch(getRegionTasksById(regionId));
    }
  }, [backgroundFormData.site?.regionId]);

  // List for flow-type updates
  useEffect(() => {
    dispatch(setIsRestrictionsPageReadOnly(jmrFlowType === JmrFlowTypes.UpdateDates));
  }, [jmrFlowType]);

  // Generate job matches and navigate to the next page
  const goToJobMatchesPage = () => {
    const items = _cloneDeep(restrictionListItems);
    let isValid = true;
    if (!isRestrictionsPageReadOnly) {
      const resp = validateRestrictionListItems(items);
      dispatch(setRestrictionListItems(resp.updatedList));
      isValid = resp.isValid;
    }
    if (isValid) {
      const jmrTasks = restrictionItemsListToJmrTaskList(items);
      dispatch(setSelectedTasks(jmrTasks));
      dispatch(closeAllToastsForMessage(TOAST_MESSAGE));
      dispatch(setPageProgress(JmrPageProgress.JobMatches));
    } else {
      dispatch(
        createToastIfNoneForMessage({
          type: 'error',
          message: TOAST_MESSAGE,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  };

  return (
    <NewJMRPageWrapper>
      {/* Main Content */}
      <NewJMRPageMainBody xs="85%" md="75%" lg="60%">
        <Column spacing="450" spacingInset="300">
          <PlacementCountAlert />
          <NewJmrPageTitle
            title="Add and Edit Restriction(s)"
            subtitle="Please add and enter all restrictions as denoted within the associates RFI."
          />
          <span style={{ pointerEvents: isRestrictionsPageReadOnly ? 'none' : 'auto' }}>
            <Column spacing="300">
              <Text>Restrictions*</Text>
              <AllTasksSelector />
              <RestrictionList />
            </Column>
          </span>
        </Column>
      </NewJMRPageMainBody>

      {/* Footer */}
      <NewJMRPageFooter
        leftSideContent={<Text>{restrictionListItems.filter((t) => !!t.label).length} Restriction(s)</Text>}
      >
        <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
          Cancel
        </Button>
        <Button type="tertiary" onClick={() => dispatch(setPageProgress(JmrPageProgress.Background))}>
          Previous
        </Button>
        <Button data-cy="new-jmr__restrictions__next" onClick={goToJobMatchesPage}>
          Next
        </Button>
      </NewJMRPageFooter>
    </NewJMRPageWrapper>
  );
};

/**
 * Convert RestrictionItemsList to JmrTask (selectedTasks) List.
 */
const restrictionItemsListToJmrTaskList = (list: RestrictionListItem[]): JmrTask[] => {
  const consolidatedTasks = list.filter(isParentTask);
  const individualTasks = list.filter(isRestrictionListTask);
  const unpackedConsolidatedTasks = _flatMap(consolidatedTasks, (task) => task.children);
  const allTasks = [...unpackedConsolidatedTasks, ...individualTasks];
  return allTasks
    .filter((t) => isNotEmpty(t.restrictedValue))
    .map(
      (task) =>
        ({
          regionId: task.regionId,
          taskId: task.taskId,
          rank: task.rank,
          label: task.label,
          description: task.description,
          identifyBodyLocation: task.identifyBodyLocation,
          measureUnit: task.measureUnit,
          restrictedValue: task.restrictedValue,
          ...(task.bodyLocationValue
            ? {
                bodyLocationValue: task.bodyLocationValue,
              }
            : {}),
        }) as JmrTask,
    );
};

// **** Export Default **** //

export default RestrictionsPage;
